import {assertUnreachable} from '@wandb/weave/common/util/types';

import {
  EventTriggeringConditionType,
  TriggerScopeType,
} from '../../../generated/graphql';
import {capitalizeFirstLetter} from '../../../util/string';
import {
  checkRegistryProject,
  isOrgRegistryProjectName,
} from '../../Registries/common/utils';
import {DEFAULT_LAUNCH_PROJECT_NAME} from '../constants';
import {getAliasFromFilterString} from './aliasUtils';
import {getRunMetricRunFilterString} from './CreateTriggerDrawer/RunFilterShared';
import {
  getRunMetricChangeString,
  RunMetricChangeConfig,
} from './CreateTriggerDrawer/RunMetricChangeSection';
import {
  getAggOpStr,
  RunMetricThresholdConfig,
} from './CreateTriggerDrawer/RunMetricThresholdSection';
import {
  getRunStateString,
  RunStateConfig,
} from './CreateTriggerDrawer/RunStateSection';
import {NotificationConfig} from './notificationUtils';
import {TriggeredActionType} from './TriggersTable/utilTypes';

type TriggerDescriptionInput = {
  eventType?: EventTriggeringConditionType;
  triggerScopeType?: TriggerScopeType;
  entityName?: string;
  projectName?: string;
  artifactCollectionName?: string;
  filters?: string;
  runMetricConfig?: RunMetricThresholdConfig;
  runMetricChangeConfig?: RunMetricChangeConfig;
  runStateConfig?: RunStateConfig;
  notificationConfig?: NotificationConfig;
};

export type DescriptionType = {
  actionType?: TriggeredActionType;
  event: {
    title: string;
    body: string;
  };
  action: {
    title: string;
    body: string;
  };
  stringVal: string;
};

export const isLegacyModelRegistryProjectName = (
  projectName: string | undefined
) => projectName === DEFAULT_LAUNCH_PROJECT_NAME;

const isUserVisibleProjectName = (projectName: string | undefined) =>
  projectName != null &&
  projectName !== '' &&
  !isOrgRegistryProjectName(projectName) &&
  !isLegacyModelRegistryProjectName(projectName);

const triggerScopeDescriptionForCollection = (
  input: TriggerDescriptionInput
) => {
  const inputRegistryInfo = checkRegistryProject(input.projectName);
  if (input.triggerScopeType === TriggerScopeType.ArtifactCollection) {
    return `the collection "${input.artifactCollectionName}".`;
  } else if (input.triggerScopeType === TriggerScopeType.Project) {
    if (inputRegistryInfo.isRegistryProject) {
      return `any collection in the ${inputRegistryInfo.registryName} registry.`;
    } else if (isLegacyModelRegistryProjectName(input.projectName)) {
      return `any artifact in the entity "${input.entityName}'s" model registry.`;
    } else if (isUserVisibleProjectName(input.projectName)) {
      // We should only hit this case for non-registry collections
      return `any collection in the project "${input.projectName}".`;
    } else {
      // we should not hit this case, but failing gracefully
      return `any collection in the registry.`;
    }
  } else if (input.triggerScopeType == null) {
    // We hit this in the New Automation wizard when the user hasn't selected a event type
    return `(event not yet configured)`;
  } else {
    assertUnreachable(input.triggerScopeType);
  }
};

// Derive default trigger 'description' based on event type and user inputs
export const getDefaultTriggerDescription = (
  input: TriggerDescriptionInput,
  actionType?: TriggeredActionType,
  jobName?: string,
  jobAlias?: string,
  webhookName?: string,
  slackChannelName?: string,
  notificationConfig?: NotificationConfig
) => {
  // Design needs a fancy way to display the description
  // Return a dictionary with the string value and the UI components
  let descriptionDict: DescriptionType = {
    event: {
      title: 'In the event that',
      body: '',
    },
    action: {
      title: '',
      body: '',
    },
    stringVal: '',
  };
  let stringStart = '';
  switch (actionType) {
    case undefined:
      return descriptionDict;
    case 'QueueJobTriggeredAction':
      if (jobName && jobAlias) {
        stringStart = `Launch the job "${jobName}:${jobAlias}" whenever `;
        descriptionDict = {
          ...descriptionDict,
          actionType: 'QueueJobTriggeredAction',
          action: {title: 'Launch the job', body: `${jobName}:${jobAlias}`},
        };
      }
      break;
    case 'GenericWebhookTriggeredAction':
      if (webhookName) {
        stringStart = `Trigger the webhook "${webhookName}" whenever `;
        descriptionDict = {
          ...descriptionDict,
          actionType: 'GenericWebhookTriggeredAction',
          action: {title: 'Trigger the webhook', body: webhookName},
        };
      }
      break;
    case 'NotificationTriggeredAction': {
      stringStart = `Send a Slack notification whenever `; // Future: add channel name
      descriptionDict = {
        ...descriptionDict,
        actionType: 'NotificationTriggeredAction',
        action: {
          title: 'Send Slack notification',
          body: slackChannelName ? `To ${slackChannelName}` : '',
        },
      };
      break;
    }
    default:
      assertUnreachable(actionType);
  }
  let stringEnd = '';
  if (input.eventType === EventTriggeringConditionType.LinkModel) {
    stringEnd = `a new version is added to ${triggerScopeDescriptionForCollection(
      input
    )}`;
  } else if (input.eventType === EventTriggeringConditionType.CreateArtifact) {
    stringEnd = `a new version of "${input.artifactCollectionName}" is created.`;
  } else if (
    input.eventType === EventTriggeringConditionType.AddArtifactAlias ||
    input.eventType === EventTriggeringConditionType.UpdateArtifactAlias
  ) {
    // In the case of the AddAlias or UpdateAlias trigger event, the first filter gives us the alias name
    const alias = getAliasFromFilterString(input.filters);
    if (input.eventType === EventTriggeringConditionType.AddArtifactAlias) {
      stringEnd = `an alias matching "${alias}" is added to ${triggerScopeDescriptionForCollection(
        input
      )}`;
    } else if (
      input.eventType === EventTriggeringConditionType.UpdateArtifactAlias
    ) {
      stringEnd = `the alias is updated to ${alias} for ${triggerScopeDescriptionForCollection(
        input
      )}`;
    } else {
      assertUnreachable(input.eventType);
    }
  } else if (input.eventType === EventTriggeringConditionType.RunMetric) {
    const runFilter = input.runMetricConfig?.runFilter;
    const metricFilter = input.runMetricConfig?.metricFilter;
    if (!metricFilter || !runFilter) {
      console.error('invalid runmetric state - missing filter parts', {
        metricFilter,
        runFilter,
      });
      return descriptionDict;
    }
    stringEnd = getRunMetricRunFilterString(runFilter);
    stringEnd += `${getAggOpStr(metricFilter.aggOp)} of "${
      metricFilter?.metricName
    }" ${metricFilter.cmpOp.label.toLowerCase()} ${
      metricFilter.threshold
    } over the last ${metricFilter.windowSize} values.`;
  } else if (input.eventType === EventTriggeringConditionType.RunMetricChange) {
    if (!input.runMetricChangeConfig) {
      console.error(
        'invalid runmetricchange state - missing runmetricchange config',
        {
          runMetricChangeConfig: input.runMetricChangeConfig,
        }
      );
      return descriptionDict;
    }
    stringEnd = getRunMetricChangeString(
      input.runMetricChangeConfig.metricFilter,
      input.runMetricChangeConfig.runFilter
    );
  } else if (input.eventType === EventTriggeringConditionType.RunState) {
    const runFilter = input.runStateConfig?.runFilter;
    const runStateFilter = input.runStateConfig?.runStateFilter;
    if (!runFilter || !runStateFilter) {
      console.error(
        'invalid runstate state - missing runfilter or runstatefilter',
        {
          runStateConfig: input.runStateConfig,
        }
      );
      return descriptionDict;
    }
    stringEnd = getRunStateString(runStateFilter, runFilter);
  } else if (input.eventType == null) {
    // this function is run before configuration is done, so it just returns defaults when
    // the data is not yet available
  } else {
    assertUnreachable(input.eventType);
  }
  descriptionDict.event.body = capitalizeFirstLetter(stringEnd.slice(0, -1)); // Remove full stop from the event body.
  descriptionDict.stringVal = stringStart + stringEnd;
  return descriptionDict;
};
